'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  Globe,
  Smartphone,
  Server,
  Megaphone,
  Headphones,
  Phone,
  Code2,
  Database,
  ChevronRight,
  Upload,
  CheckCircle2,
  ArrowLeft,
  X,
  FileText,
} from 'lucide-react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


const serviceCategories = [
  {
    id: 'web-dev',
    label: 'Web Development',
    icon: Globe,
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/30',
    description: 'Websites, portals, e-commerce, landing pages',
  },
  {
    id: 'mobile-app',
    label: 'Mobile App',
    icon: Smartphone,
    color: 'text-cyan',
    bg: 'bg-cyan/10',
    border: 'border-cyan/30',
    description: 'iOS, Android, cross-platform applications',
  },
  {
    id: 'custom-software',
    label: 'Custom Software',
    icon: Code2,
    color: 'text-purple-400',
    bg: 'bg-purple-400/10',
    border: 'border-purple-400/30',
    description: 'ERP, CRM, automation, custom platforms',
  },
  {
    id: 'cloud-hosting',
    label: 'Cloud & Hosting',
    icon: Server,
    color: 'text-success',
    bg: 'bg-success/10',
    border: 'border-success/30',
    description: 'Cloud migration, hosting, DevOps, infrastructure',
  },
  {
    id: 'digital-marketing',
    label: 'Digital Marketing',
    icon: Megaphone,
    color: 'text-warning',
    bg: 'bg-warning/10',
    border: 'border-warning/30',
    description: 'SEO, Google Ads, social media, content marketing',
  },
  {
    id: 'bpo',
    label: 'BPO Services',
    icon: Headphones,
    color: 'text-orange-400',
    bg: 'bg-orange-400/10',
    border: 'border-orange-400/30',
    description: 'Call center, data entry, back-office operations',
  },
  {
    id: 'voip',
    label: 'VOIP / Call Center',
    icon: Phone,
    color: 'text-teal-400',
    bg: 'bg-teal-400/10',
    border: 'border-teal-400/30',
    description: 'SIP calling, IVR, call routing, campaigns',
  },
  {
    id: 'api-integration',
    label: 'API Integration',
    icon: Database,
    color: 'text-indigo-400',
    bg: 'bg-indigo-400/10',
    border: 'border-indigo-400/30',
    description: 'Third-party integrations, system connectivity',
  },
];

const budgetRanges = [
  'Under $5,000',
  '$5,000 – $15,000',
  '$15,000 – $50,000',
  '$50,000 – $100,000',
  '$100,000+',
  'Not sure yet',
];

const timelineOptions = [
  'ASAP (within 2 weeks)',
  '1 month',
  '2–3 months',
  '3–6 months',
  '6+ months',
  'Flexible',
];

type Step = 1 | 2 | 3;

export default function RequestServicePage() {
  const [step, setStep] = useState<Step>(1);
  const [selectedService, setSelectedService] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    projectName: '',
    description: '',
    budget: '',
    timeline: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    additionalNotes: '',
  });
  const [files, setFiles] = useState<string[]>([]);

  const handleNext = () => {
    if (step < 3) setStep((s) => (s + 1) as Step);
  };

  const handleBack = () => {
    if (step > 1) setStep((s) => (s - 1) as Step);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const handleFileAdd = () => {
    setFiles((prev) => [...prev, `document_${prev.length + 1}.pdf`]);
  };

  const handleFileRemove = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  if (submitted) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-background flex items-center justify-center px-6">
          <div className="card-surface p-10 max-w-md w-full text-center">
            <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 size={32} className="text-success" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Request Submitted!</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Your service request has been received. Our team will review it and get back to you within 24 hours.
            </p>
            <p className="text-xs font-mono text-muted-foreground bg-muted/40 rounded-lg px-3 py-2 mb-6">
              Reference: REQ-2026-{Math.floor(Math.random() * 900 + 100)}
            </p>
            <div className="flex gap-3">
              <Link href="/client-portal" className="flex-1">
                <button className="w-full px-4 py-2.5 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted/40 transition-colors">
                  Back to Dashboard
                </button>
              </Link>
              <Link href="/client-portal/projects" className="flex-1">
                <button className="w-full px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors">
                  Track Projects
                </button>
              </Link>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-6 py-6 max-w-screen-lg mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link href="/client-portal">
            <button className="p-2 rounded-lg border border-border hover:bg-muted/40 transition-colors text-muted-foreground hover:text-foreground">
              <ArrowLeft size={16} />
            </button>
          </Link>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Client Portal</p>
            <h1 className="text-xl font-bold text-foreground">Request a Service / Submit a Project</h1>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-8">
          {([1, 2, 3] as Step[]).map((s) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${step >= s ? 'text-primary' : 'text-muted-foreground'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${step > s ? 'bg-primary border-primary text-white' : step === s ? 'border-primary text-primary' : 'border-border text-muted-foreground'}`}>
                  {step > s ? <CheckCircle2 size={14} /> : s}
                </div>
                <span className="text-xs font-medium hidden sm:block">
                  {s === 1 ? 'Select Service' : s === 2 ? 'Project Details' : 'Contact & Submit'}
                </span>
              </div>
              {s < 3 && <div className={`flex-1 h-px ${step > s ? 'bg-primary' : 'bg-border'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Service Selection */}
        {step === 1 && (
          <div>
            <p className="text-sm text-muted-foreground mb-5">Select the type of service you need. You can request multiple services in separate submissions.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              {serviceCategories.map((svc) => {
                const Icon = svc.icon;
                const isSelected = selectedService === svc.id;
                return (
                  <button
                    key={svc.id}
                    onClick={() => setSelectedService(svc.id)}
                    className={`card-surface p-4 text-left transition-all border-2 ${isSelected ? `${svc.border} bg-card` : 'border-transparent hover:border-border'}`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${svc.bg} flex items-center justify-center mb-3`}>
                      <Icon size={20} className={svc.color} />
                    </div>
                    <p className="text-sm font-semibold text-foreground mb-1">{svc.label}</p>
                    <p className="text-2xs text-muted-foreground leading-relaxed">{svc.description}</p>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleNext}
                disabled={!selectedService}
                className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Project Details */}
        {step === 2 && (
          <div>
            <div className="card-surface p-6 mb-6">
              <h2 className="text-sm font-semibold text-foreground mb-5">Project Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Project / Request Name *</label>
                  <input
                    type="text"
                    value={form.projectName}
                    onChange={(e) => setForm((f) => ({ ...f, projectName: e.target.value }))}
                    placeholder="e.g. Company Website Redesign"
                    className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Project Description *</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    rows={4}
                    placeholder="Describe your project requirements, goals, and any specific features you need..."
                    className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Budget Range</label>
                  <select
                    value={form.budget}
                    onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
                    className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="">Select budget range</option>
                    {budgetRanges.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Expected Timeline</label>
                  <select
                    value={form.timeline}
                    onChange={(e) => setForm((f) => ({ ...f, timeline: e.target.value }))}
                    className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="">Select timeline</option>
                    {timelineOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Additional Notes</label>
                  <textarea
                    value={form.additionalNotes}
                    onChange={(e) => setForm((f) => ({ ...f, additionalNotes: e.target.value }))}
                    rows={3}
                    placeholder="Any references, inspirations, or special requirements..."
                    className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors resize-none"
                  />
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div className="card-surface p-6 mb-6">
              <h2 className="text-sm font-semibold text-foreground mb-4">Attachments (Optional)</h2>
              <button
                type="button"
                onClick={handleFileAdd}
                className="w-full border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center gap-2 hover:border-primary/40 transition-colors text-muted-foreground hover:text-foreground"
              >
                <Upload size={22} />
                <p className="text-sm font-medium">Click to upload documents</p>
                <p className="text-2xs">PDF, DOCX, PNG, JPG up to 10MB each</p>
              </button>
              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-muted/30 rounded-lg px-3 py-2">
                      <FileText size={14} className="text-muted-foreground flex-shrink-0" />
                      <span className="text-xs text-foreground flex-1">{file}</span>
                      <button onClick={() => handleFileRemove(idx)} className="text-muted-foreground hover:text-danger transition-colors">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <button onClick={handleBack} className="flex items-center gap-2 px-5 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/40 transition-colors">
                <ArrowLeft size={15} /> Back
              </button>
              <button
                onClick={handleNext}
                disabled={!form.projectName || !form.description}
                className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Contact & Submit */}
        {step === 3 && (
          <form onSubmit={handleSubmit}>
            <div className="card-surface p-6 mb-6">
              <h2 className="text-sm font-semibold text-foreground mb-5">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    value={form.contactName}
                    onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))}
                    placeholder="James Okafor"
                    className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Email Address *</label>
                  <input
                    type="email"
                    value={form.contactEmail}
                    onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))}
                    placeholder="james@pinnaclesys.com"
                    className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    value={form.contactPhone}
                    onChange={(e) => setForm((f) => ({ ...f, contactPhone: e.target.value }))}
                    placeholder="+1 (555) 000-0000"
                    className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="card-surface p-6 mb-6">
              <h2 className="text-sm font-semibold text-foreground mb-4">Request Summary</h2>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Service Type</span>
                  <span className="text-foreground font-medium">{serviceCategories.find(s => s.id === selectedService)?.label}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Project Name</span>
                  <span className="text-foreground font-medium">{form.projectName || '—'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Budget</span>
                  <span className="text-foreground font-medium">{form.budget || '—'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Timeline</span>
                  <span className="text-foreground font-medium">{form.timeline || '—'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Attachments</span>
                  <span className="text-foreground font-medium">{files.length} file(s)</span>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button type="button" onClick={handleBack} className="flex items-center gap-2 px-5 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/40 transition-colors">
                <ArrowLeft size={15} /> Back
              </button>
              <button
                type="submit"
                disabled={!form.contactName || !form.contactEmail}
                className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Submit Request <CheckCircle2 size={15} />
              </button>
            </div>
          </form>
        )}
      </div>
    </AppLayout>
  );
}
